import React, { useCallback, useEffect, useState } from "react";

import { useLocation, useParams } from "react-router-dom";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { httpService } from "../../../../../../base/services/httpService.service";
import OnlineIndicator from "./OnlineIndicator";
import PreviewModal from "./PreviewModal";
import FullscreenPageIcon from "./FullscreenPageIcon";
import DescriptionWithButton from "../../../../../../ui/DescriptionWithButton/DescriptionWithButton";

import "./PreviewBanner.scss";

dayjs.extend(advancedFormat);

const PreviewBanner = (props) => {
   const { id, uuid } = useParams();
   const location = useLocation();
   const currentPath = location.pathname;

   const [isModalOpen, setIsModalOpen] = useState({
      details: false,
      onlineStatus: false
   });
   const [surveyBannerData, setSurveyBannerData] = useState({
      name: "",
      description: "",
      endDate: "",
      logo: "",
      backgroundImage: "",
      textColor: "",
      backgroundColor: ""
   });

   const handleToggleModal = (modalName) => () => {
      setIsModalOpen((prevState) => ({
         ...prevState,
         [modalName]: !prevState[modalName]
      }));
   };

   const { onLoad } = props;

   const getBannerData = useCallback(() => {
      let params =
         uuid || currentPath.includes("test")
            ? new URLSearchParams({ surveyId: id })
            : new URLSearchParams({ submissionId: id });

      httpService.get(
         `Survey/GetBannerData?${params.toString()}`,
         (response) => {
            setSurveyBannerData(response.data);
            // Preview reads the survey's colours off the same response.
            onLoad?.(response.data);
         },
         // A banner that can't be read keeps the default look. When the survey
         // itself can't be shown, the page below says why, so a respondent gets
         // one message instead of two.
         () => {}
      );
   }, [currentPath, id, onLoad, uuid]);

   useEffect(() => {
      getBannerData();
   }, [getBannerData]);

   return (
      <>
         <div
            className={`preview-banner ${!props.loading ? "preview-banner__show" : ""}`}
            style={{
               position: "relative",
               backgroundColor: surveyBannerData.backgroundColor || "#5770CB",
               backgroundImage: surveyBannerData.backgroundImage
                  ? `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${surveyBannerData.backgroundImage})`
                  : "none",
               backgroundRepeat: "no-repeat",
               backgroundSize: "cover",
               backgroundPosition: "center"
            }}
         >
            <div className="preview-banner--info">
               <div
                  className={`preview-banner--info---status${
                     uuid !== "multi-use-kiosk" || !document.fullscreenEnabled
                        ? " preview-banner--info---extra-status-class"
                        : ""
                  }`}
               >
                  <div>{surveyBannerData.logo && <img src={surveyBannerData.logo} alt="" />}</div>
                  <OnlineIndicator
                     indicatorClickHandler={handleToggleModal("onlineStatus")}
                     color={surveyBannerData.textColor || "#FFFFFF"}
                  />
               </div>
               <div className="preview-banner--info---desc">
                  {uuid !== "multi-use-kiosk" && (
                     <h5
                        style={{
                           color: surveyBannerData.textColor || "#FFFFFF"
                        }}
                     >
                        Deadline:{" "}
                        {surveyBannerData.endDate
                           ? dayjs(surveyBannerData.endDate).format("MMMM Do, YYYY")
                           : "-"}
                     </h5>
                  )}
                  <h2
                     style={{
                        color: surveyBannerData.textColor || "#FFFFFF"
                     }}
                  >
                     {surveyBannerData.name}
                  </h2>
                  <p
                     dangerouslySetInnerHTML={{
                        __html: (surveyBannerData.description || "").replace(/\n/g, "<br />")
                     }}
                     style={{
                        color: surveyBannerData.textColor || "#FFFFFF"
                     }}
                  />
                  <DescriptionWithButton
                     description={surveyBannerData.description}
                     title={surveyBannerData.name}
                     textColor={surveyBannerData.textColor || "#FFFFFF"}
                  />
               </div>
               {uuid === "multi-use-kiosk" && document.fullscreenEnabled && <FullscreenPageIcon />}
            </div>
            {/* <div className="preview-banner--image">
               <img
                  src={toAbsoluteUrl("/media/svg/survey/PreviewBanner.svg")}
                  alt="Survey banner"
               />
            </div> */}
         </div>

         <>
            {/* Survey details Modal - START */}
            <PreviewModal
               hasCloseButton
               title={surveyBannerData.name}
               isModalOpen={isModalOpen.details}
               setIsModalOpen={handleToggleModal("details")}
            >
               <div
                  dangerouslySetInnerHTML={{
                     __html: (surveyBannerData.description || "").replace(/\n/g, "<br />")
                  }}
               />
            </PreviewModal>
            {/* Survey details Modal - END */}

            {/* Online status modal - START */}
            <PreviewModal
               hasCloseButton
               title={"Form works offline!"}
               isModalOpen={isModalOpen.onlineStatus}
               setIsModalOpen={handleToggleModal("onlineStatus")}
            >
               <div>
                  <div>
                     This form is now fully accessible even without an Internet connection on your
                     device. Make sure to bookmark it for quick offline access anytime.
                  </div>
                  <br />
                  <div>
                     Your records are securely stored and queued directly in your browser until
                     you're back online. Once you revisit your app after gaining internet
                     connection, all records will be automatically submitted.
                  </div>
                  <br />
                  <div>
                     Only after a record has been successfully submitted, it will be removed from
                     the queue. You can safely close down your browser and device with items in the
                     queue.
                  </div>
               </div>
            </PreviewModal>
            {/* Survey details Modal - END */}
         </>
      </>
   );
};

export default React.memo(PreviewBanner);
